import { Component, inject, Inject, OnInit, signal, computed, model } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { Country, Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { AccountManualType } from "@bitwarden/web-vault/app/models/types/account.types";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { InstitutionService } from "@bitwarden/web-vault/app/services/DataService/institution/institution.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { GlossSync } from "@bitwarden/web-vault/app/services/syncing/gloss.sync";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { AVAILABLE_PLUGINS } from "@bitwarden/web-vault/app/models/data/plugin-metadata";
import { matchesRegionalPluginSearch, REGIONAL_SYSTEM_PLUGINS } from "./account-regions-connectors";

@Component({
  selector: "app-account-regions",
  templateUrl: "./account-regions.component.html",
})
export class AccountRegionsComponent implements OnInit {
  protected filteredCountries: Country[] = [];
  protected installedPluginIds = signal<string[]>([]);
  protected searchQuery = model<string>("");
  /** True from the Fiskil tile click until connect() settles (success redirects the page). */
  protected isConnectingToFiskil = signal(false);
  private appState = inject(AppStateStoreService);
  private dialogFactory = new DialogFactory();
  private dialogRef = inject(MatDialogRef);
  private pluginConfigService = inject(PluginConfigService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private roleAccessService = inject(RoleAccessService);
  private router = inject(Router);

  /** Regional bank connectors shown in the account-regions dialog (searchable). */
  private readonly regionalSystemPlugins = REGIONAL_SYSTEM_PLUGINS;

  protected showFiskil = computed(() =>
    matchesRegionalPluginSearch(this.searchQuery(), "fiskil", this.regionalSystemPlugins),
  );

  protected showPlaid = computed(() =>
    matchesRegionalPluginSearch(this.searchQuery(), "plaid", this.regionalSystemPlugins),
  );

  /** Search match and BETA_ACCESS — aligned with template visibility for hasAnyResults. */
  protected showPlaidTile = computed(() => this.roleAccessService.isBetaUser() && this.showPlaid());

  protected showRegionalConnectors = computed(() => this.showFiskil() || this.showPlaidTile());

  // Computed signal for filtered plugins - automatically updates when dependencies change
  protected filteredInstalledPluginIds = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.installedPluginIds();
    }

    return this.installedPluginIds().filter((pluginId) => {
      const metadata = AVAILABLE_PLUGINS.find((p) => p.id === pluginId);
      return (
        metadata &&
        (metadata.displayName.toLowerCase().includes(query) ||
          metadata.id.toLowerCase().includes(query))
      );
    });
  });

  // Computed: Check if there are any results
  protected hasAnyResults = computed(() => {
    return this.showRegionalConnectors() || this.filteredInstalledPluginIds().length > 0;
  });

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      accountType: AccountManualType; // threaded through for back-navigation continuity; not used for display here
      isFromGuide: boolean;
      closeDialog: CallableFunction;
      backClicked: CallableFunction;
      setLoading: CallableFunction;
    },
    private globalService: GlobalService,
    private institutionService: InstitutionService,
    private glossSync: GlossSync,
  ) {}

  async ngOnInit() {
    this.filteredCountries = await this.institutionService.getCountryMasterList();
    this.loadInstalledPlugins();
  }

  private loadInstalledPlugins() {
    const pluginConfigs = this.pluginConfigService.getAllConfigs();

    // Normalize function to remove spaces and hyphens for comparison
    const normalize = (str: string) => str?.toLowerCase().replace(/[\s-]/g, "") ?? "";

    // Get enabled plugin IDs
    const enabledPluginIds = AVAILABLE_PLUGINS.filter((plugin) => {
      const config = pluginConfigs.find(
        (c) =>
          normalize(c.name) === normalize(plugin.id) ||
          normalize(c.name) === normalize(plugin.displayName),
      );
      return config?.enabled || false;
    }).map((plugin) => plugin.id);

    this.installedPluginIds.set(enabledPluginIds);
  }

  async goWithBasiq() {
    try {
      this.appState.startProcess("basiq-syncing-data", "Redirecting you to Basiq");
      this.data.closeDialog();
      this.data.setLoading(true);

      await this.glossSync.connect(Origin.basiq);

      this.appState.endProcess("basiq-syncing-data");
      this.data.setLoading(false);
    } catch (e) {
      this.appState.endProcess("basiq-syncing-data");
      this.globalService.showErrorMessage("error", "somethingWentWrong");
      this.data.setLoading(false);
    }
  }

  async goWithFiskil() {
    // Re-entry guard: connect() runs several API round-trips before the redirect, and a second
    // click would start a second auth session.
    if (this.isConnectingToFiskil()) {
      return;
    }
    // The dialog stays open (showing the loading state on the Fiskil tile) until connect()
    // settles; on success the page is already navigating to Fiskil by then.
    this.isConnectingToFiskil.set(true);
    // Esc / backdrop must not dismiss the dialog mid-connect.
    this.dialogRef.disableClose = true;
    try {
      this.appState.startProcess("fiskil-syncing-data", "Redirecting you to Fiskil");
      this.data.setLoading(true);

      await this.glossSync.connect(Origin.fiskil, this.data.isFromGuide);

      this.appState.endProcess("fiskil-syncing-data");
      this.data.setLoading(false);
    } catch (e) {
      this.appState.endProcess("fiskil-syncing-data");
      this.globalService.showErrorMessage("error", "somethingWentWrong");
      this.data.setLoading(false);
    } finally {
      this.isConnectingToFiskil.set(false);
      // Close this dialog, not data.closeDialog(): that targets the factory's current dialogRef,
      // which may point at a different dialog by now.
      this.dialogRef.close();
    }
  }

  async goWithPlaid() {
    try {
      this.appState.startProcess("plaid-syncing-data", "Connecting to Plaid");
      this.data.closeDialog();
      this.data.setLoading(true);

      await this.glossSync.connect(Origin.plaid, this.data.isFromGuide);

      this.data.setLoading(false);
    } catch (e) {
      this.appState.endProcess("plaid-syncing-data");
      this.globalService.showErrorMessage("error", "somethingWentWrong");
      this.data.setLoading(false);
    }
  }

  onPluginCardClick(pluginId: string) {
    // Close the current dialog and open the plugin config dialog for the specific plugin ID
    this.data.closeDialog();
    this.dialogFactory.openPluginConfigDialog(pluginId, {
      accountType: this.data.accountType,
    });
  }

  getBanks(country: string): string[] {
    const banks: { [key: string]: string[] } = {
      // todo make the regions > country > banks classes
      AU: ["cba", "anzb", "nab"],
      US: ["wfo", "ctg", "boa"],
      HK: ["hsbc", "scb", "boc"],
    };

    return banks[country];
  }

  // todo: goes to plugin page atm, which breaks the flow, design is do a pop up that has the plugin page style (wont break the flow)
  async goToPluginsPage() {
    const confirmed = await this.confirmationDialog.confirmFor(
      ConfirmationEnum.leavingCreateAccountFlow,
    );

    if (confirmed) {
      this.data.closeDialog();
      this.router.navigate(["/", GlossRoutes.Plugins], { queryParams: { tab: "store" } });
    }
  }

  get contentClass(): string {
    return this.data.isFromGuide
      ? "tw-relative tw-z-[100] tw-bg-color-surface-card tw-rounded-2xl tw-p-4 min-md:tw-w-[70%]"
      : "tw-w-full";
  }

  onBackClick() {
    // Back mid-connect would open another dialog while the connect is still in flight.
    if (this.isConnectingToFiskil()) {
      return;
    }
    this.data.closeDialog();
    this.data.backClicked();
  }
}
